import React, { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import { faPaperPlane } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql, readInlineData } from "relay-runtime";

import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { useStreamSettingsModal } from "~/stream/StreamSettings";
import Menu from "~/ui/menu";
import Switch from "~/ui/Switch/Switch";
import Tooltip from "~/ui/Tooltip";

import { type AbstractAction, ActionButton } from "./shared";

import type { AutoSendToEveryone_autoShareIsSetToEveryone_note$key } from "~/__generated__/AutoSendToEveryone_autoShareIsSetToEveryone_note.graphql";
import type { AutoSendToEveryone_note$key } from "~/__generated__/AutoSendToEveryone_note.graphql";

import styles from "./AutoSendToEveryone.module.css";

const ACTION_ID = "auto-send-to-everyone" as const;

export function autoShareIsSetToEveryone(noteKey: AutoSendToEveryone_autoShareIsSetToEveryone_note$key): boolean {
	const note = readInlineData(
		graphql`
			fragment AutoSendToEveryone_autoShareIsSetToEveryone_note on Note @inline {
				stream {
					meetingRecapAutoShareSource {
						__typename
						... on StreamMeetingRecapAutoShareSettings {
							sendTo_S: sendTo
						}
						... on UserMeetingRecapAutoShareSettings {
							sendTo_U: sendTo
						}
					}
				}
			}
		`,
		noteKey,
	);
	const autoShareSource = note.stream.meetingRecapAutoShareSource;
	if (!autoShareSource) return false;
	return (
		(autoShareSource.__typename === "StreamMeetingRecapAutoShareSettings" && autoShareSource.sendTo_S === "ALL") ||
		(autoShareSource.__typename === "UserMeetingRecapAutoShareSettings" && autoShareSource.sendTo_U === "ALL")
	);
}

const enableMutation = graphql`
	mutation AutoSendToEveryoneEnableMutation($streamId: ID!) {
		updateStreamMeetingRecapAutoShareSettings(sendTo: ALL, streamId: $streamId) {
			settings {
				id
				sendTo_S: sendTo
				setBy {
					id
					fullName
					firstName
				}
			}
			stream {
				id
				meetingRecapAutoShareSource {
					__typename
					... on StreamMeetingRecapAutoShareSettings {
						id
						sendTo_S: sendTo
						setBy {
							id
							fullName
							firstName
						}
					}
					... on UserMeetingRecapAutoShareSettings {
						id
						sendTo_U: sendTo
						user {
							id
							fullName
							firstName
						}
					}
				}
			}
		}
	}
`;

export async function enableAutoShareToEveryone(streamId: string): Promise<void> {
	await commitMutationPromise(environment, {
		mutation: enableMutation,
		variables: {
			streamId,
		},
	});
}

export interface AutoSendToEveryoneProps {
	note: AutoSendToEveryone_note$key;
}

const noteFragment = graphql`
	fragment AutoSendToEveryone_note on Note {
		stream {
			id
		}
		...AutoSendToEveryone_autoShareIsSetToEveryone_note
	}
`;

function useAutoShare(noteKey: AutoSendToEveryone_note$key) {
	const note = useFragment(noteFragment, noteKey);
	const toggled = autoShareIsSetToEveryone(note);
	const [loading, setLoading] = useState(false);
	const openStreamSettings = useStreamSettingsModal(note.stream.id, ["automations", "MEETING_RECAP_AUTO_SHARE"]);
	const onToggle = useCallback(async () => {
		if (!toggled) {
			setLoading(true);
			try {
				await enableAutoShareToEveryone(note.stream.id);
			} finally {
				setLoading(false);
			}
		} else {
			openStreamSettings();
		}
	}, [note.stream.id, openStreamSettings, toggled]);
	return [toggled, loading, onToggle] as const;
}

function InfoTooltip({ children, toggled }: { children: React.ReactElement; toggled: boolean }) {
	return (
		<Tooltip
			placement="top"
			maxWidth={250}
			className={styles.tooltip}
			content={
				<>
					<div className={styles.header}>Send AI recap to attendees</div>
					<p>
						{toggled ? "The" : "When enabled, the"} notes taken by the Note Taker during the meeting will be sent to all
						attendees.
					</p>
				</>
			}
		>
			{children}
		</Tooltip>
	);
}

function Button({ note: noteKey }: AutoSendToEveryoneProps) {
	const [toggled, loading, onToggle] = useAutoShare(noteKey);
	return (
		<InfoTooltip toggled={toggled}>
			<ActionButton actionId={ACTION_ID} aria-pressed={toggled} onClick={onToggle} disabled={loading}>
				<FontAwesomeIcon icon={faPaperPlane} />
				&ensp;Send AI recap to attendees&ensp;
				<Switch tabIndex={-1} checked={toggled} aria-hidden size="tiny" />
			</ActionButton>
		</InfoTooltip>
	);
}

function MenuItem({ note: noteKey }: AutoSendToEveryoneProps) {
	const [toggled, loading, onToggle] = useAutoShare(noteKey);
	return (
		<InfoTooltip toggled={toggled}>
			<Menu.SwitchItem icon={faPaperPlane} checked={toggled} disabled={loading} onSelect={onToggle} closeOnSelect>
				Send AI recap to attendees
			</Menu.SwitchItem>
		</InfoTooltip>
	);
}

export default {
	id: ACTION_ID,
	button: Button,
	menuItem: MenuItem,
} satisfies AbstractAction;
